import { useMemo, useState } from "react";

import { ArrowBack, ChevronRight, SearchOffRounded } from "@mui/icons-material";
import {
  Card,
  IconButton,
  List,
  ListItemButton,
  TextField,
} from "@mui/material";

type SearchableChoiceListProps<T> = {
  items: T[];
  getKey: (item: T) => string;
  getSearchText: (item: T) => string;
  getTitle: (item: T) => string;
  getDescription: (item: T) => string | null | undefined;
  getMetadata?: (item: T) => Array<string | null | undefined>;
  getDisabledReason?: (item: T) => string | null | undefined;
  onSelect: (item: T) => void;
  onBack?: () => void;
  heading: string;
  countLabel: string;
  searchPlaceholder: string;
  emptyMessage: string;
};

const SearchableChoiceList = <T,>({
  items,
  getKey,
  getSearchText,
  getTitle,
  getDescription,
  getMetadata,
  getDisabledReason,
  onSelect,
  onBack,
  heading,
  countLabel,
  searchPlaceholder,
  emptyMessage,
}: SearchableChoiceListProps<T>) => {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      getSearchText(item).toLowerCase().includes(query)
    );
  }, [getSearchText, items, search]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        {onBack && (
          <IconButton
            onClick={onBack}
            size="small"
            className="text-neutral-500"
            aria-label="Back"
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <span className="text-base font-medium text-neutral-700">
          {heading}
        </span>
        <span className="text-xs text-neutral-400 ml-auto">{countLabel}</span>
      </div>

      {items.length > 0 && (
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          fullWidth
          autoFocus
          inputProps={{ "aria-label": "Search offers" }}
        />
      )}

      {items.length === 0 || filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-neutral-400">
          <SearchOffRounded className="text-4xl" />
          <p className="text-sm">
            {items.length === 0
              ? emptyMessage
              : `No offers matching “${search}”`}
          </p>
        </div>
      ) : (
        <List
          component={Card}
          disablePadding
          className="overflow-auto max-h-[50vh]"
        >
          {filtered.map((item, index) => {
            const disabledReason = getDisabledReason?.(item);
            const metadata = (getMetadata?.(item) ?? []).filter(
              (value): value is string => Boolean(value)
            );
            return (
              <ListItemButton
                key={getKey(item)}
                onClick={() => onSelect(item)}
                disabled={Boolean(disabledReason)}
                divider={index < filtered.length - 1}
                className="py-4 px-5 transition-colors flex flex-col items-start gap-1.5 group"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="font-semibold text-blue-600 text-base">
                    {getTitle(item)}
                  </div>
                  {!disabledReason && (
                    <ChevronRight className="text-neutral-300 group-hover:text-blue-500 transition-colors" />
                  )}
                </div>
                <div className="text-sm text-neutral-700 w-full pr-6 leading-relaxed line-clamp-2">
                  {getDescription(item) || (
                    <span className="italic text-neutral-400">
                      No description
                    </span>
                  )}
                </div>
                {metadata.length > 0 && (
                  <div className="text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-2">
                    {metadata.join(" • ")}
                  </div>
                )}
                {disabledReason && (
                  <div className="text-sm font-medium text-red-700">
                    Unavailable: {disabledReason}
                  </div>
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </div>
  );
};

export default SearchableChoiceList;
